import { Effect, Either, Schema } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import {
  CreateKitchenStaffRequestSchema,
  CreateKitchenShiftResponseSchema,
  effectiveStaffAccessExpiry,
  KitchenStaffAccountParamsSchema,
  KitchenStaffAccountListSchema,
  KitchenStaffAccountSchema,
  StaffAccessTokenClaimsSchema,
  StaffLanguageSchema,
  StaffRoleSchema,
  type StaffLanguage,
  type StaffRole,
} from "../../../packages/common/src/schemas/auth.ts";
import {
  makeBetterAuthClient,
  type BetterAuthClient,
} from "#better-auth-client.ts";

/** Safe, classified HTTP or session failure. */
export class ApiError extends Schema.TaggedError<ApiError>()("ApiError", {
  status: Schema.Number,
  message: Schema.String,
}) {
  constructor(
    status: number,
    message: string,
  ) {
    super({ status, message });
  }
}

/** Expected failures are values callers must handle. */
export type Result<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ApiError };

/** Keeps credentials out of accidental JSON/log output. */
export class Credential {
  #value: string;
  constructor(value: string) {
    this.#value = value;
  }
  /** Reveal only at an HTTP or WebSocket boundary. */
  reveal() {
    return this.#value;
  }
  /** Safe diagnostic representation. */
  toJSON() {
    return "[Redacted]";
  }
  /** Safe diagnostic representation. */
  toString() {
    return "[Redacted]";
  }
}

/** Public browser endpoints, with no API keys. */
export type ClientConfig = {
  readonly authProvider: "legacy" | "better-auth";
  readonly apiBase: string;
  readonly realtime: string;
  readonly eventsHost: string;
};

/** Parse deployment configuration at startup. */
export function clientConfig(stage: unknown): Result<ClientConfig> {
  if (stage === "local")
    return {
      ok: true,
      value: {
        authProvider: "legacy",
        apiBase: "http://localhost:8787",
        realtime: "ws://localhost:8788",
        eventsHost: "localhost:8788",
      },
    };

  if (stage !== "sandbox" && stage !== "prod")
    return {
      ok: false,
      error: new ApiError(0, "Landpad deployment stage is missing or invalid."),
    };

  const id =
    stage === "sandbox"
      ? "wpho5wbeuzeyjpanfkhvalgqb4"
      : "yk455v3ljrf7tbzxe5zh3lygwu";

  return {
    ok: true,
    value: {
      authProvider: "better-auth",
      apiBase:
        stage === "sandbox"
          ? "https://api.dev.pocketrice.com.au"
          : "https://api.pocketrice.com.au",
      realtime: `wss://${id}.appsync-realtime-api.ap-southeast-2.amazonaws.com/event/realtime`,
      eventsHost: `${id}.appsync-api.ap-southeast-2.amazonaws.com`,
    },
  };
}

/** Decoded HTTP output; schema mechanics stay at the boundary. */
export type ApiRequest<T, I = T> = {
  readonly path: string;
  readonly method?: "DELETE" | "GET" | "POST" | "PUT";
  readonly body?: unknown;
  readonly idempotencyKey?: string;
  readonly schema: Schema.Schema<T, I, never>;
  readonly signal?: AbortSignal;
};

const errorBody = Schema.Struct({ message: Schema.optional(Schema.String) });

const authBody = Schema.Struct({
  accessToken: Schema.String.pipe(Schema.minLength(1)),
});

const betterAuthTokenBody = Schema.Struct({
  token: Schema.String.pipe(Schema.minLength(1)),
});

export type PasskeySummary = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string | undefined;
};

/** Safe account metadata returned by the Better Auth admin plugin. */
export type StaffAccountSummary = typeof KitchenStaffAccountSchema.Type;

const legacyClaims = Schema.Struct({
  exp: Schema.Number.pipe(Schema.greaterThan(0)),
  userId: Schema.NonEmptyString,
  username: Schema.NonEmptyString,
});
const claims = Schema.Union(StaffAccessTokenClaimsSchema, legacyClaims);

/** In-memory access session. Refresh credentials remain in HTTP-only cookies. */
type AuthenticatedSessionBase = {
  readonly kind: "authenticated";
  readonly credential: Credential;
  readonly expiresAt: number;
  readonly userId: string;
  readonly username: string;
  readonly language: StaffLanguage;
  readonly role: StaffRole;
};

export type Session =
  | { readonly kind: "loading" }
  | { readonly kind: "anonymous" }
  | (AuthenticatedSessionBase & { readonly sessionKind: "direct" })
  | (AuthenticatedSessionBase & {
      readonly sessionKind: "kitchen-shift";
      readonly issuedBy: string;
      readonly shiftExpiresAt: number;
      readonly role: "kitchen-staff";
    });

/** Shared HTTP/session adapter. Renewal is deduplicated; only rejected 401s retry. */
export class StaffApi {
  #session: Session = { kind: "loading" };
  #listeners = new Set<() => void>();
  #renewal: Promise<Result<Credential>> | undefined;
  #generation = 0;
  #betterAuthClient: BetterAuthClient;
  constructor(
    readonly config: ClientConfig,
    private readonly http: typeof fetch = (input, init) => fetch(input, init),
    private readonly now = Date.now,
  ) {
    this.#betterAuthClient = makeBetterAuthClient(config.apiBase, http);
  }
  get supportsPasskeys() {
    return this.config.authProvider === "better-auth";
  }
  /** React external-store snapshot. */
  snapshot = (): Session => this.#session;
  /** Observe session transitions. */
  subscribe = (listener: () => void) => {
    this.#listeners.add(listener);

    return () => {
      this.#listeners.delete(listener);
    };
  };
  private setSession(session: Session) {
    this.#session = session;
    this.#listeners.forEach((listener) => listener());
  }
  /** Anonymous or order-scoped requests never borrow staff credentials. */
  async publicRequest<T, I>(
    input: ApiRequest<T, I>,
    credential?: Credential,
  ): Promise<Result<T>> {
    const result = await this.sendRequest(input, credential);

    // Reading is safe to retry after a short outage. Never replay a mutation
    // whose response was lost: the server may already have saved the change.
    if (
      (input.method ?? "GET") !== "GET" ||
      result.ok ||
      ![0, 502, 503, 504].includes(result.error.status) ||
      input.signal?.aborted
    )
      return result;
    await new Promise<void>((resolve) => setTimeout(resolve, 500));

    if (input.signal?.aborted) return result;

    return this.sendRequest(input, credential);
  }
  /** Effect-native public request for query, mutation, and workflow composition. */
  publicRequestEffect<T, I>(
    input: ApiRequest<T, I>,
    credential?: Credential,
  ): Effect.Effect<T, ApiError> {
    return Effect.promise(() => this.publicRequest(input, credential)).pipe(
      Effect.flatMap((result) =>
        result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
      ),
      Effect.withSpan("StaffApi.publicRequest", {
        attributes: {
          "http.request.method": input.method ?? "GET",
        },
      }),
    );
  }
  private async sendRequest<T, I>(
    input: ApiRequest<T, I>,
    credential?: Credential,
  ): Promise<Result<T>> {
    const controller = new AbortController();
    const abort = () => controller.abort();
    input.signal?.addEventListener("abort", abort, { once: true });

    if (input.signal?.aborted) abort();
    const timeout = setTimeout(abort, 15_000);

    try {
      const response = await this.http(`${this.config.apiBase}${input.path}`, {
        method: input.method ?? "GET",
        credentials: "include",
        signal: controller.signal,
        headers: {
          ...(credential
            ? { Authorization: `Bearer ${credential.reveal()}` }
            : {}),
          ...(input.body === undefined
            ? {}
            : { "Content-Type": "application/json" }),
          ...(input.idempotencyKey
            ? { "Idempotency-Key": input.idempotencyKey }
            : {}),
        },
        body: input.body === undefined ? undefined : JSON.stringify(input.body),
      });

      const raw = await response.text();
      let body: unknown;

      try {
        body = raw ? JSON.parse(raw) : null;
      } catch {
        body = undefined;
      }

      if (!response.ok) {
        const parsed = decodeUnknownOption(errorBody, body);

        const fallback =
          response.status === 403
            ? "Your account does not have permission for this action."
            : response.status === 401
              ? input.path === "/auth/login"
                ? "Incorrect username or password."
                : "Your session has expired. Please sign in again."
              : response.status === 429
                ? "The service is busy. Wait a moment, then try again."
                : "The request could not be completed. Please try again.";

        return {
          ok: false,
          error: new ApiError(
            response.status,
            // A rejected sign-in is not an expired session. Keep the message
            // identical for missing users/wrong passwords; don't expose backend
            // error details or allow stale session wording to override it.
            response.status === 401 && input.path === "/auth/login"
              ? fallback
              : parsed
                ? (parsed.message ?? fallback)
                : fallback,
          ),
        };
      }

      const parsed = Schema.decodeUnknownEither(input.schema)(body);

      return Either.isRight(parsed)
        ? { ok: true, value: parsed.right }
        : {
            ok: false,
            error: new ApiError(
              502,
              "The server returned an unreadable response. Please refresh.",
            ),
          };
    } catch {
      return {
        ok: false,
        error: new ApiError(
          0,
          input.method === "PUT" ||
            (input.method === "POST" && !input.path.startsWith("/auth/"))
            ? "Connection interrupted. Your change may have been saved. Refresh to check before trying again."
            : "Connection interrupted. Check your connection and try again.",
        ),
      };
    } finally {
      clearTimeout(timeout);
      input.signal?.removeEventListener("abort", abort);
    }
  }
  private async authenticate(
    path: string,
    body?: unknown,
  ): Promise<Result<Credential>> {
    const generation = this.#generation;

    const result = await this.publicRequest({
      path,
      method: "POST",
      body,
      schema: authBody,
    });

    return result.ok
      ? this.acceptAccessToken(result.value.accessToken, generation)
      : this.rejectAuthentication(result.error);
  }
  private rejectAuthentication(error: ApiError): Result<Credential> {
    if (
      error.status === 401 ||
      error.status === 403 ||
      this.#session.kind === "loading"
    )
      this.setSession({ kind: "anonymous" });

    return { ok: false, error };
  }
  private acceptAccessToken(
    accessToken: string,
    generation = this.#generation,
  ): Result<Credential> {
    if (generation !== this.#generation)
      return { ok: false, error: new ApiError(401, "Session ended.") };

    try {
      const payload = accessToken.split(".")[1];

      const decodedResult = payload
        ? Schema.decodeUnknownEither(claims)(
            JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))),
          )
        : undefined;
      const decoded =
        decodedResult && Either.isRight(decodedResult)
          ? decodedResult.right
          : undefined;
      const staffClaims = decoded
        ? Schema.decodeUnknownEither(StaffAccessTokenClaimsSchema)(decoded)
        : undefined;
      const staffIdentity =
        staffClaims && Either.isRight(staffClaims)
          ? staffClaims.right
          : undefined;

      const expiresAt = decoded
        ? (staffIdentity
            ? effectiveStaffAccessExpiry(staffIdentity)
            : decoded.exp) * 1_000
        : 0;
      if (!decoded || expiresAt <= this.now()) {
        this.setSession({ kind: "anonymous" });

        return {
          ok: false,
          error: new ApiError(502, "Invalid sign-in response."),
        };
      }
      const decodedRole = Schema.decodeUnknownEither(StaffRoleSchema)(
        "role" in decoded ? decoded.role : "admin",
      );
      if (Either.isLeft(decodedRole))
        return {
          ok: false,
          error: new ApiError(502, "Invalid staff role in sign-in response."),
        };

      const credential = new Credential(accessToken);
      const identity = {
        kind: "authenticated",
        credential,
        expiresAt,
        userId: decoded.userId,
        username: decoded.username,
        language: staffIdentity?.language ?? "en",
        role: decodedRole.right,
      } as const;
      this.setSession(
        staffIdentity?.sessionKind === "kitchen-shift"
          ? {
              ...identity,
              role: "kitchen-staff",
              sessionKind: staffIdentity.sessionKind,
              issuedBy: staffIdentity.issuedBy,
              shiftExpiresAt: staffIdentity.shiftExpiresAt * 1_000,
            }
          : { ...identity, sessionKind: "direct" },
      );

      return { ok: true, value: credential };
    } catch {
      this.setSession({ kind: "anonymous" });

      return {
        ok: false,
        error: new ApiError(502, "Invalid sign-in response."),
      };
    }
  }
  private async betterAuthCredential(): Promise<Result<Credential>> {
    const generation = this.#generation;
    const token = await this.publicRequest({
      path: "/auth/v2/token",
      schema: betterAuthTokenBody,
    });

    return token.ok
      ? this.acceptAccessToken(token.value.token, generation)
      : this.rejectAuthentication(token.error);
  }
  /** Share one refresh across concurrent requests. */
  refresh = (): Promise<Result<Credential>> => {
    if (!this.#renewal)
      this.#renewal = (
        this.config.authProvider === "better-auth"
          ? this.betterAuthCredential()
          : this.authenticate("/auth/refresh")
      ).finally(() => {
        this.#renewal = undefined;
      });

    return this.#renewal;
  };
  /** Obtain a current credential, including after tablet sleep. */
  async credential(): Promise<Result<Credential>> {
    const session = this.#session;
    if (
      session.kind === "authenticated" &&
      session.sessionKind === "kitchen-shift" &&
      session.expiresAt > this.now() &&
      session.shiftExpiresAt <= this.now() + 60_000
    )
      return { ok: true, value: session.credential };

    return session.kind === "authenticated" &&
      session.expiresAt > this.now() + 60_000
      ? { ok: true, value: session.credential }
      : this.refresh();
  }
  /** Sign in with staff credentials and exchange the cookie session for a JWT. */
  login = async (username: string, password: string) => {
    if (this.config.authProvider === "legacy")
      return this.authenticate("/auth/login", { username, password });

    const generation = this.#generation;
    const result = await this.#betterAuthClient.signIn.username({
      username,
      password,
      rememberMe: true,
    });
    if (result.error)
      return this.rejectAuthentication(
        new ApiError(
          result.error.status,
          result.error.status === 401
            ? "Incorrect username or password."
            : (result.error.message ?? "Sign-in could not be completed."),
        ),
      );
    if (generation !== this.#generation)
      return this.rejectAuthentication(new ApiError(401, "Session ended."));

    return this.betterAuthCredential();
  };
  /** Sign in using a discoverable WebAuthn credential. */
  loginWithPasskey = async (): Promise<Result<Credential>> => {
    if (!this.supportsPasskeys)
      return {
        ok: false,
        error: new ApiError(400, "Passkeys are not enabled in this environment."),
      };
    const result = await this.#betterAuthClient.signIn.passkey();
    if (result.error)
      return this.rejectAuthentication(
        new ApiError(
          result.error.status,
          result.error.message ?? "Passkey sign-in was cancelled.",
        ),
      );

    return this.betterAuthCredential();
  };
  /** Redeem a single-use token into an HTTP-only Better Auth session. */
  loginWithOneTimeToken = async (
    token: string,
  ): Promise<Result<Credential>> => {
    if (!this.supportsPasskeys)
      return this.rejectAuthentication(
        new ApiError(400, "Device sign-in is not enabled here."),
      );
    const result = await this.#betterAuthClient.oneTimeToken.verify({ token });
    if (result.error)
      return this.rejectAuthentication(
        new ApiError(
          result.error.status,
          result.error.message ??
            "This device sign-in code is invalid or expired.",
        ),
      );

    return this.betterAuthCredential();
  };
  /** List passkeys without exposing Better Auth's transport types to the UI. */
  async listPasskeys(): Promise<Result<readonly PasskeySummary[]>> {
    const result = await this.#betterAuthClient.passkey.listUserPasskeys();
    if (result.error)
      return {
        ok: false,
        error: new ApiError(
          result.error.status,
          result.error.message ?? "Passkeys could not be loaded.",
        ),
      };

    return {
      ok: true,
      value: result.data.map((item) => ({
        id: item.id,
        name: item.name?.trim() || "Unnamed passkey",
        createdAt: item.createdAt ? String(item.createdAt) : undefined,
      })),
    };
  }
  /** Register a platform or roaming passkey for the current session. */
  async addPasskey(name: string): Promise<Result<PasskeySummary>> {
    const result = await this.#betterAuthClient.passkey.addPasskey({ name });
    if (result.error)
      return {
        ok: false,
        error: new ApiError(
          result.error.status,
          result.error.message ?? "Passkey registration was cancelled.",
        ),
      };

    return {
      ok: true,
      value: {
        id: result.data.id,
        name: result.data.name?.trim() || "Unnamed passkey",
        createdAt: result.data.createdAt
          ? String(result.data.createdAt)
          : undefined,
      },
    };
  }
  /** Remove one passkey owned by the current user. */
  async deletePasskey(id: string): Promise<Result<void>> {
    const result = await this.#betterAuthClient.passkey.deletePasskey({ id });
    return result.error
      ? {
          ok: false,
          error: new ApiError(
            result.error.status,
            result.error.message ?? "The passkey could not be removed.",
          ),
        }
      : { ok: true, value: undefined };
  }
  /** List least-privilege kitchen accounts for administrator settings. */
  async listKitchenStaff(): Promise<Result<readonly StaffAccountSummary[]>> {
    return this.request({
      path: "/auth/kitchen-staff",
      schema: KitchenStaffAccountListSchema,
    });
  }
  /** Issue a one-use QR into an eight-hour kitchen shift session. */
  async createKitchenShift(userId: string): Promise<
    Result<{
      readonly token: Credential;
      readonly handoffExpiresAt: number;
      readonly shiftExpiresAt: number;
    }>
  > {
    const result = await this.request({
      path: "/auth/kitchen-shifts",
      method: "POST",
      body: { userId },
      schema: CreateKitchenShiftResponseSchema,
    });
    if (!result.ok) return result;

    return {
      ok: true,
      value: {
        ...result.value,
        token: new Credential(result.value.token),
      },
    };
  }
  /** Create a credential-free kitchen identity controlled by shift QR issuance. */
  async createKitchenStaff(input: {
    readonly username: string;
  }): Promise<Result<StaffAccountSummary>> {
    const parsedInput = Schema.decodeUnknownEither(
      CreateKitchenStaffRequestSchema,
    )(input);
    if (Either.isLeft(parsedInput))
      return {
        ok: false,
        error: new ApiError(
          400,
          "Use 3–64 letters, numbers, dots, underscores, or hyphens.",
        ),
      };

    return this.request({
      path: "/auth/kitchen-staff",
      method: "POST",
      body: parsedInput.right,
      schema: KitchenStaffAccountSchema,
    });
  }
  /** Permanently remove a kitchen identity and its stored sessions. */
  async deleteKitchenStaff(userId: string): Promise<Result<void>> {
    const parsedInput = Schema.decodeUnknownEither(
      KitchenStaffAccountParamsSchema,
    )({ userId });
    if (Either.isLeft(parsedInput))
      return {
        ok: false,
        error: new ApiError(400, "The kitchen account is invalid."),
      };

    const result = await this.request({
      path: `/auth/kitchen-staff/${encodeURIComponent(parsedInput.right.userId)}`,
      method: "DELETE",
      schema: Schema.Null,
    });
    return result.ok ? { ok: true, value: undefined } : result;
  }
  /** Save and immediately apply the current user's interface language. */
  async updateLanguage(language: StaffLanguage): Promise<Result<void>> {
    const parsedLanguage = Schema.decodeUnknownEither(StaffLanguageSchema)(
      language,
    );
    if (Either.isLeft(parsedLanguage))
      return {
        ok: false,
        error: new ApiError(400, "The selected language is invalid."),
      };
    if (!this.supportsPasskeys)
      return {
        ok: false,
        error: new ApiError(
          400,
          "Saved language settings require Better Auth.",
        ),
      };

    const result = await this.publicRequest({
      path: "/auth/v2/update-user",
      method: "POST",
      body: { language: parsedLanguage.right },
      schema: Schema.Struct({ status: Schema.Literal(true) }),
    });
    if (!result.ok) return result;

    const current = this.#session;
    if (current.kind === "authenticated")
      this.setSession({ ...current, language: parsedLanguage.right });

    return { ok: true, value: undefined };
  }
  /** End local access before invalidating the refresh cookie. */
  async logout(): Promise<Result<unknown>> {
    this.#generation++;
    this.setSession({ kind: "anonymous" });

    if (this.config.authProvider === "better-auth") {
      const result = await this.#betterAuthClient.signOut();
      return result.error
        ? {
            ok: false,
            error: new ApiError(
              result.error.status,
              result.error.message ?? "Sign out could not be completed.",
            ),
          }
        : { ok: true, value: result.data };
    }

    return this.publicRequest({
      path: "/auth/logout",
      method: "POST",
      schema: Schema.Unknown,
    });
  }
  /** Renew/retry once only when authentication rejected the request. */
  async request<T, I>(input: ApiRequest<T, I>): Promise<Result<T>> {
    const credential = await this.credential();

    if (!credential.ok) return credential;
    const result = await this.publicRequest(input, credential.value);

    if (result.ok || result.error.status !== 401) return result;
    const current = this.#session;

    const refreshed =
      current.kind === "authenticated" &&
      current.credential !== credential.value
        ? { ok: true as const, value: current.credential }
        : await this.refresh();

    if (!refreshed.ok) return refreshed;
    const retried = await this.publicRequest(input, refreshed.value);

    if (!retried.ok && retried.error.status === 401)
      this.setSession({ kind: "anonymous" });

    return retried;
  }
  /** Effect-native authenticated request with a typed failure channel. */
  requestEffect<T, I>(
    input: ApiRequest<T, I>,
  ): Effect.Effect<T, ApiError> {
    return Effect.promise(() => this.request(input)).pipe(
      Effect.flatMap((result) =>
        result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
      ),
      Effect.withSpan("StaffApi.request", {
        attributes: {
          "http.request.method": input.method ?? "GET",
        },
      }),
    );
  }
}
